<template>
  <draggable tag="div"
             class="v-gallery"
             :class="{ 'v-gallery--has-any-selected': !!selected }"
             v-bind="{ group: 'groups', draggable: outerGroupDraggableSelector, delay: 100, delayOnTouchOnly: true, touchStartThreshold: 3, fallbackTolerance: 3, filter: selectorIsFiltered }"
             :force-fallback="true"
             :fallback-on-body="true"
             v-model="dataCollection"
             @update="$emit('reorder-group', dataCollection.map(i => i.id), galleryMemberId); $emit('reorder', reorder())">
    <div class="v-gallery__group v-gallery__group-item"
         v-for="(group, index) in dataCollection"
         :key="group.id"
         :class="{ 'v-gallery__group--busy': isBusy(group.id) }"
         :aria-busy="isBusy(group.id) ? 'true' : null">
      <div>
        <div class="v-gallery__tools" v-if="toolbar">
          <a class="add-facility" @click="toggleAddForm(group.id)" v-if="addForm" :id="group.id" style="cursor:pointer;" title="Add" aria-label="Add">
            <i class="material-icons">add_circle_outline</i>
          </a>
          <a @click="toggleToolbar(group.id)" style="cursor:pointer;" v-if="editButton" title="Edit" aria-label="Edit">
            <img style="vertical-align: top;" src="/images/cms_wijzigen.png">
          </a>
          <a @click="!isBusy(group.id) && $emit('delete-group', group.id, galleryMemberId)" style="cursor:pointer;" v-if="removeButton" title="Delete" aria-label="Delete">
            <img style="vertical-align: top;" src="/images/cms_verwijderen.png">
          </a>
          <slot name="extra" :group="group"></slot>
        </div>
        <slot name="header" :group="group">
          <h3 class="v-gallery__heading">
            {{ group[groupName] }}
          </h3>
        </slot>
      </div>
      <div class="v-gallery__media">
        <div class="loader-bar" v-if="singleGallery && showLoaderFor === group.id"></div>
        <div class="loader-bar loader-bar--loop" v-else style="display: none;"></div>
        <div v-if="group.toolbar">
          <slot name="toolbar" :item="group"></slot>
        </div>
        <div class="mt-3"
             v-else-if="group[items] && group[items].includes(selected)">

          <slot name="toolbar" :selected="selected" :facility="group"></slot>
        </div>
        <draggable tag="ul"
                   :draggable="selector"
                   class="v-gallery__list"
                   v-model="group[items]"
                   v-bind="{ group: 'items', delay: 75, delayOnTouchOnly: true, touchStartThreshold: 3, fallbackTolerance: 3, filter: selectorIsFiltered, disabled: isBusy(group.id) }"
                   :force-fallback="true"
                   :fallback-on-body="true"
                   @update="$emit('reorder-item', group[items].map(i => i.id), group.id, galleryMemberId); $emit('reorder', reorder())"
                   @add="$emit('reorder-outer-item', group[items].map(i => i.id), group.id, galleryMemberId)"
                   @change="$event.removed || $event.added ? $emit('change', $event, group.id, galleryMemberId) : null">

          <template v-for="item in group[items]">
            <li class="v-gallery__item v-gallery__item--draggable"
                :class="{ 'v-gallery__item--is-selected': selected === item }"
                :key="item.id">
              <div class="v-gallery__holder">
                <div class="v-gallery__inner">
                  <slot :gallery-id="galleryMemberId" :item="item" :group="group" :groupheader="group[groupName]" :is-selected="selected === item"></slot>
                </div>
              </div>
              <v-lightcase-holder :image-src="item.url" :name="`lightcase-${item.id}`" :download-link="item.fileUrl" :current-document="item" :disable-rotate="disableRotate" :details="group"></v-lightcase-holder>
            </li>
          </template>
          <li :for="`file${group.id}`"
              class="v-gallery__item v-gallery__item-to-drop"
              v-if="!selected"
              @dragover="resetEvent($event)"
              @dragleave="resetEvent($event)"
              @drop="handleUpload($event, group.id, galleryMemberId)"
              @click="triggerFileInputClick(`fileInput${group.id}`)">
            <div class="v-gallery__holder">
              <div class="v-gallery__inner">
                <div class="v-gallery__plus">
                  +
                </div>
              </div>
            </div>
          </li>
          <li :for="`file${group.id}`"
              class="v-gallery__item v-gallery__item-to-drop"
              v-if="!selected"
              v-show="isMobile"
              @click="triggerFileInputClick(`cameraFileInput${group.id}`)">
            <div class="v-gallery__holder">
              <div class="v-gallery__inner">
                <div class="v-gallery__plus">
                  <i class="material-icons">party_mode</i>
                </div>
              </div>
            </div>
          </li>
          <li class="v-gallery__item v-gallery__item-to-drop"
              v-if="!selected && showCameraLoopBatch"
              @click="$emit('cameraloop-batch', group.id)"
              title="Batch foto's"
              aria-label="Batch foto's">
            <div class="v-gallery__holder">
              <div class="v-gallery__inner">
                <div class="v-gallery__plus">
                  <i class="material-icons">burst_mode</i>
                </div>
              </div>
            </div>
          </li>
          <li class="v-gallery__item v-gallery__item-to-drop v-gallery__item-dump-picker"
              v-if="!selected && showDumpPicker"
              @click="$emit('open-dump-picker', group.id, galleryMemberId)"
              :title="dumpPickerLabel"
              :aria-label="dumpPickerLabel">
            <div class="v-gallery__holder">
              <div class="v-gallery__inner">
                <div class="v-gallery__plus">
                  <i class="material-icons">photo_library</i>
                </div>
              </div>
            </div>
          </li>
        </draggable>
        <input type="file"
               @change="handleUpload($event, group.id, galleryMemberId)"
               accept="image/*"
               capture="camera"
               v-show="false"
               :ref="`cameraFileInput${group.id}`">
        <input type="file" v-if="acceptImages"
               multiple
               @change="handleUpload($event, group.id, galleryMemberId)"
               accept="image/*"
               v-show="false"
               :ref="`fileInput${group.id}`">
        <input type="file" v-else
               multiple
               @change="handleUpload($event, group.id, galleryMemberId)"
               v-show="false"
               :ref="`fileInput${group.id}`">


      </div>
      <slot name="row-detail" :group="group" :index="index"></slot>
      <div v-if="group.addForm" style="display: flex;">
        <slot name="add-form" :item="group"></slot>
      </div>
    </div>
  </draggable>
</template>

<script>
  import _ from "lodash";
  import MobileDetect from "mobile-detect/mobile-detect";
  import draggable from "vuedraggable";
  import "lightcase/src/css/lightcase.css";
  import configureLightcase from "../../lightcase.js";
  import VLightcaseHolder from "./VLightcaseHolder";
  import sweetalert from 'sweetalert';

  window.MobileDetect = MobileDetect;

  export default {
    components: {
      draggable,
      VLightcaseHolder
    },
    data() {
      return {
        selected: null,
        dataCollection: Object.assign([], this.collection),
        // Match only real photo tiles. Upload/camera placeholders use
        // .v-gallery__item-to-drop and must stay non-draggable so iOS Sortable
        // does not pick them up on touchstart.
        selector: ".v-gallery__item--draggable",
        selectorIsFiltered: '.v-gallery__item-to-drop',
        isMobile: false
      };
    },
    computed: {
      // Outer group draggable selector reacts to isMobile (set in created()).
      // Returns a non-existent selector to disable outer drag, or the real
      // group selector to enable it. Inner photo drag is independent.
      outerGroupDraggableSelector() {
        if (this.disableDragAndDrop || (this.disableOuterGroupReorderOnTouch && this.isMobile)) {
          return '.none-of-current-items-draggable';
        }
        return '.v-gallery__group-item';
      }
    },
    methods: {
      // A group whose media is being changed server-side (busyGroups[id] is set): no upload,
      // reorder, camera, Dump pick or group delete until the caller clears it.
      isBusy(groupId) {
        return !!this.busyGroups && this.busyGroups[String(groupId)] !== undefined;
      },
      reorder() {
        return this.dataCollection.map(group => {
          let groupItems = {};
          groupItems[group.id] = (group[this.items] || []).map(i => i.id);
          return groupItems;
        });
      },
      ensureGroupItemsArray() {
        (this.dataCollection || []).forEach(group => {
          if (group && !Array.isArray(group[this.items])) {
            this.$set(group, this.items, []);
          }
        });
      },
      toggleToolbar(groupId) {
        let currentGroupIndex = this.dataCollection.findIndex(item => {
          return item.id === groupId;
        });
        let currentGroup = this.dataCollection[currentGroupIndex];
        currentGroup.toolbar = !currentGroup.toolbar;
        this.dataCollection[currentGroupIndex] = {
          ...currentGroup
        };
        this.$emit("toolbar-toggled", {
          index: currentGroupIndex,
          state: !!currentGroup.toolbar
        });
        this.dataCollection = [...this.dataCollection];
      },
      toggleAddForm(groupId) {
        let currentGroupIndex = this.dataCollection.findIndex(item => {
          return item.id === groupId;
        });
        let currentGroup = this.dataCollection[currentGroupIndex];
        currentGroup.addForm = !currentGroup.addForm;
        this.dataCollection[currentGroupIndex] = {
          ...currentGroup
        };
        this.dataCollection = [...this.dataCollection];
      },
      resetEvent(event) {
        if (!window.app.methods.isTouchDevice()) {
          event.stopPropagation();
          event.preventDefault();
        }
      },
      triggerFileInputClick(ref) {
        this.$refs[ref][0].click();
      },
      handleUpload(event, groupId, galleryId) {
        event.preventDefault();
        event.stopPropagation();
        if (this.isBusy(groupId)) {
          return;
        }

        let files,
          filesNumber = 0;

        filesNumber = event.target.closest('.v-gallery__group-item').querySelectorAll('.v-gallery__item--draggable').length;

        if (
          event.dataTransfer &&
          event.dataTransfer.files &&
          event.dataTransfer.files.length
        )
        {
          files = event.dataTransfer.files;

          if (!this.allowSingleFileOnly) {
            this.$emit("upload", files, groupId, galleryId);
          }
          else {
            if (filesNumber === 0 && event.dataTransfer.files.length === 1) {
              this.$emit("upload", files, groupId, galleryId);
            } else {
              sweetalert('Information', 'You may upload only one file', 'info');
            }
          }
        } else if (
          event.target &&
          event.target.files &&
          event.target.files.length
        )
        {
          files = event.target.files;

          if (!this.allowSingleFileOnly) {
            this.$emit("upload", files, groupId, galleryId);
          }
          else {
            if (filesNumber === 0 && event.target.files.length === 1) {
              this.$emit("upload", files, groupId, galleryId);
            } else {
              sweetalert('Information', 'You may upload only one file', 'info');
            }
          }
        }
      }
    },
    props: {
      collection: {
        default: () => [],
        type: Array
      },
      galleryMemberId: {
        default: 0,
        type: Number
      },
      items: {
        default: "items",
        type: String
      },
      groupName: {
        default: "name",
        type: String
      },
      toolbar: {
        default: false,
        type: Boolean
      },
      addForm: {
        default: false,
        type: Boolean
      },
      editButton: {
        default: true,
        type: Boolean
      },
      removeButton: {
        default: true,
        type: Boolean
      },
      // Opt-in per caller. When true, an additional cell is rendered next
      // to the + / Camera placeholders in each group's photo grid. Click
      // emits `cameraloop-batch` with the group's id. Default false so
      // other VGallery surfaces (inspections, members, etc.) are
      // unaffected.
      showCameraLoopBatch: {
        default: false,
        type: Boolean
      },
      // Opt-in per caller. When true, an additional "Choose from Dump" cell is
      // rendered next to the + / Camera placeholders, emitting
      // `open-dump-picker` (groupId, galleryMemberId) on click. Default false so
      // every other VGallery surface is unaffected unless it opts in.
      showDumpPicker: {
        default: false,
        type: Boolean
      },
      dumpPickerLabel: {
        default: 'Foto dump',
        type: String
      },
      disableRotate: {
        default: false,
        type: Boolean
      },
      showLoaderFor: {
        default: 0,
        type: Number
      },
      disableDragAndDrop: {
        default: false,
        type: Boolean
      },
      disableOuterGroupReorderOnTouch: {
        default: false,
        type: Boolean
      },
      acceptImages: {
        default: false,
        type: Boolean
      },
      singleGallery: {
        default: true,
        type: Boolean
      },
      allowSingleFileOnly: {
        default: false,
        type: Boolean
      },
      // Opt-in: {groupId: anything} for groups whose media actions are locked while a request
      // runs (the property pages' "remove all photos of this room"). Default: nothing locked.
      busyGroups: {
        default: () => ({}),
        type: Object
      },
    },
    created() {
      var md = new MobileDetect(window.navigator.userAgent);
      if (md.mobile() !== "" && md.mobile() !== null) {
        this.isMobile = true;
      }
      if (!this.singleGallery) {
        $(document).on('click', '.v-gallery__plus', function () {
          $('.loader-bar--loop').removeClass('is-visible');
          $(this).parents('.v-gallery__group-item').find('.loader-bar--loop').addClass('is-visible');
        });
      }
      this.ensureGroupItemsArray();
      this._onCloseAddForm = (groupId) => {
        const idx = this.dataCollection.findIndex(item => item.id === groupId);
        if (idx !== -1 && this.dataCollection[idx].addForm) {
          this.dataCollection[idx] = {...this.dataCollection[idx], addForm: false};
          this.dataCollection = [...this.dataCollection];
        }
      };
      this.$root.$on('close-add-form', this._onCloseAddForm);
    },
    watch: {
      collection(value) {
        this.dataCollection = Object.assign([], value || []);
        this.ensureGroupItemsArray();
      }
    },
    beforeMount() {
      configureLightcase();
    },
    beforeDestroy() {
      if (this._onCloseAddForm) {
        this.$root.$off('close-add-form', this._onCloseAddForm);
      }
    },
    beforeUpdate() {
      configureLightcase();
    },
    updated() {
      this.$root.$emit("galleryUpdated");
    }
  };
</script>

<style>
  /* A busy group (busyGroups) keeps its photos visible but takes no clicks, drops or drags. */
  .v-gallery__group--busy .v-gallery__media,
  .v-gallery__group--busy .v-gallery__tools {
    pointer-events: none;
    opacity: .55;
  }
  .v-gallery__deleting-photos {
    display: inline-block;
    max-width: 100%;
    white-space: normal;
    font-weight: 600;
  }
  .v-gallery__deleting-photos::before {
    content: "";
    display: inline-block;
    width: .9em;
    height: .9em;
    margin-right: .4em;
    vertical-align: -.1em;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: v-gallery-deleting-spin .8s linear infinite;
  }
  @keyframes v-gallery-deleting-spin {
    to { transform: rotate(360deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .v-gallery__deleting-photos::before { animation: none; }
  }
  /* iOS Sortable touch reorder: claim the pointer on photo tiles so the
     browser does not scroll/zoom or open the long-press callout. Applied
     only to real photo items; upload/camera tiles keep native behaviour
     so tap-to-upload still works. */
  .v-gallery__item--draggable {
    touch-action: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
</style>
